<script lang="ts">
  import { onMount } from 'svelte';
  import Screen from '../lib/design/Screen.svelte';
  import ApiKeysCard from '../lib/design/ApiKeysCard.svelte';
  import SecurityCard from '../lib/design/SecurityCard.svelte';
  import { link, href } from '../lib/router.svelte';
  import { session, ApiError, DROPBOX_START_PATH } from '../lib/session.svelte';
  import { getUsage, type Usage } from '../lib/usage';
  import { count, usd } from '../lib/format';
  import { nest } from '../lib/nest.svelte';
  import { storageEstimate, persistStorage } from '../lib/pwa.svelte';
  import { install, isIos } from '../lib/install.svelte';
  import { theme } from '../lib/theme.svelte';

  let busy = $state<'' | 'dropbox' | 'signout' | 'rebuild'>('');
  let error = $state('');

  const s = $derived(session.state);

  let usage = $state<Usage | null>(null);
  let usageErr = $state(false);
  let store = $state<{ usage: number; quota: number } | null>(null);
  let persisted = $state(false);

  onMount(async () => {
    getUsage()
      .then((u) => (usage = u))
      .catch(() => (usageErr = true));
    store = await storageEstimate();
    try {
      persisted = (await navigator.storage?.persisted?.()) ?? false;
    } catch {
      persisted = false;
    }
  });

  const mb = (n: number) => `${(n / 1_048_576).toFixed(1)} MB`;
  function pct(used: number, cap: number | null): number | null {
    return cap ? Math.min(100, Math.round((used / cap) * 100)) : null;
  }

  async function rebuild() {
    if (busy) return;
    busy = 'rebuild';
    await nest.rebuild();
    busy = '';
  }

  async function enablePersist() {
    persisted = await persistStorage();
  }

  function connectDropbox() {
    // Full-page navigation into the backend's PKCE flow; it redirects back here.
    location.href = DROPBOX_START_PATH;
  }

  async function disconnectDropbox() {
    if (busy) return;
    busy = 'dropbox';
    error = '';
    try {
      await session.disconnectDropbox();
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Could not disconnect. Try again.';
    } finally {
      busy = '';
    }
  }

  async function signOut() {
    if (busy) return;
    busy = 'signout';
    await session.logout();
    // App swaps to <Connect /> once session.ready flips to false.
  }
</script>

<Screen title="Settings">
  <div class="card">
    <p class="eyebrow">Account</p>
    <p class="value">{s.email ?? '—'}</p>
    <button class="ghost" onclick={signOut} disabled={busy !== ''}>
      {busy === 'signout' ? 'Signing out…' : 'Sign out'}
    </button>
  </div>

  <div class="card">
    <p class="eyebrow">Appearance</p>
    <button class="row" onclick={() => theme.toggle()} role="switch" aria-checked={theme.dark}>
      <span class="value">Theme</span>
      <span class="mono muted">{theme.dark ? 'dark' : 'light'}</span>
    </button>
  </div>

  <div class="card">
    <p class="eyebrow">Storage</p>
    {#if s.dropboxConnected}
      <p class="value">
        Dropbox — connected{s.dropboxAccountEmail ? ` as ${s.dropboxAccountEmail}` : ''}
      </p>
      {#if s.coopPath}
        <p class="muted">Coop folder: <code>{s.coopPath}</code></p>
      {/if}
      <button class="ghost" onclick={disconnectDropbox} disabled={busy !== ''}>
        {busy === 'dropbox' ? 'Disconnecting…' : 'Disconnect'}
      </button>
    {:else}
      <p class="value">Dropbox — not connected</p>
      <p class="muted">Connect Dropbox to read your nest and save captures.</p>
      <button class="cta" onclick={connectDropbox} disabled={busy !== ''}>Connect Dropbox</button>
    {/if}
  </div>

  <div class="card">
    <p class="eyebrow">Kip usage &amp; billing</p>
    {#if usage}
      <p class="value">{usage.plan} plan · {usd(usage.usage.cost_usd)} this period</p>
      <p class="muted">
        {count(usage.usage.total_tokens)} tokens · {count(usage.usage.calls)} calls this period
      </p>
      {#if usage.limits.monthly_token_cap}
        {@const p = pct(usage.usage.total_tokens, usage.limits.monthly_token_cap)}
        <div class="bar"><span style="width:{p}%"></span></div>
        <p class="muted">{p}% of your {count(usage.limits.monthly_token_cap)}-token cap</p>
      {/if}
      <a class="detail" use:link href={href('/usage')}>Usage &amp; billing →</a>
    {:else if usageErr}
      <p class="muted">Couldn't load usage.</p>
    {:else}
      <p class="muted">Loading…</p>
    {/if}
  </div>

  <ApiKeysCard />
  <SecurityCard />

  <div class="card">
    <p class="eyebrow">On-device</p>
    <p class="value">{nest.count} pages cached</p>
    {#if store}
      <p class="muted">
        {mb(store.usage)} used · {persisted
          ? 'kept safe from cleanup'
          : 'may be cleared under storage pressure'}
      </p>
    {/if}
    <div class="btns">
      {#if !persisted}
        <button class="ghost" onclick={enablePersist}>Keep offline copy</button>
      {/if}
      <button class="ghost" onclick={rebuild} disabled={busy !== '' || !s.dropboxConnected}>
        {busy === 'rebuild' ? 'Rebuilding…' : 'Rebuild from Dropbox'}
      </button>
    </div>
  </div>

  {#if !install.installed}
    <div class="card">
      <p class="eyebrow">Install</p>
      {#if install.canPrompt}
        <p class="muted">Add Kip to your home screen for a full-screen, offline-capable app.</p>
        <button class="cta" onclick={() => install.prompt()}>Add to home screen</button>
      {:else if isIos}
        <p class="muted">
          In Safari: tap the Share button, then <strong>Add to Home Screen</strong>.
        </p>
      {:else}
        <p class="muted">
          Use your browser's menu → <strong>Install app</strong> / Add to home screen.
        </p>
      {/if}
    </div>
  {/if}

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <p class="about eyebrow">Kip PWA · companion to the Kip desktop app</p>
</Screen>

<style>
  .card {
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 14px;
    background: var(--cream-2);
    box-shadow: var(--shadow-soft);
  }
  .card .eyebrow {
    margin-bottom: 6px;
  }
  .value {
    font-size: 15px;
  }
  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 2px 0;
  }
  .muted {
    color: var(--ink-soft);
    font-size: 14px;
    margin-top: 4px;
  }
  code {
    font-family: var(--mono);
    background: var(--paper);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .cta,
  .ghost {
    margin-top: 12px;
    border-radius: 10px;
    padding: 9px 16px;
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
  }
  .cta {
    background: var(--rooster);
    color: var(--cream);
  }
  .ghost {
    border: 1px solid var(--line);
    color: var(--ink-soft);
  }
  .cta:disabled,
  .ghost:disabled {
    opacity: 0.4;
  }
  .btns {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .btns .ghost {
    margin-top: 12px;
  }
  .bar {
    margin-top: 10px;
    height: 6px;
    border-radius: 999px;
    background: var(--paper);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--rooster);
  }
  .detail {
    display: inline-block;
    margin-top: 10px;
    color: var(--rooster);
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .error {
    color: var(--rooster-deep);
    font-size: 13px;
    margin-bottom: 10px;
  }
  .about {
    margin-top: 24px;
    color: var(--ink-faint);
  }
</style>
